import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FaPlay, FaPause, FaVolumeUp, FaStepForward, FaStepBackward } from 'react-icons/fa';
import '../styles/SharePage.css';

function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const audioRef = useRef(null);

  const getCoverUrl = (coverUrl) => {
    if (!coverUrl) return null;
    if (coverUrl.startsWith('http')) return coverUrl;
    const cleanPath = coverUrl.startsWith('/') ? coverUrl : `/${coverUrl}`;
    return `https://disney-music.gpworld.fr${cleanPath}`;
  };

  const getAudioUrl = (filePath) => {
    if (!filePath) return '';
    if (filePath.startsWith('http')) return filePath;
    const cleanPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
    return `https://disney-music.gpworld.fr${cleanPath}`;
  };

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    try {
      const response = await axios.get(`/api/share/public/${token}`);
      setData(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Lien invalide');
      setLoading(false);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      if (data?.type === 'album' && currentTrackIndex < data.tracks.length - 1) {
        setCurrentTrackIndex(prev => prev + 1);
      } else {
        setIsPlaying(false);
      }
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [data, currentTrackIndex]);

  // Charger la piste quand l'index change (pour albums)
  useEffect(() => {
    if (!data || !audioRef.current) return;
    
    let trackPath;
    if (data.type === 'track') {
      trackPath = data.track.file_path;
    } else if (data.type === 'album' && data.tracks[currentTrackIndex]) {
      trackPath = data.tracks[currentTrackIndex].file_path;
    }

    if (trackPath) {
      audioRef.current.src = getAudioUrl(trackPath);
      audioRef.current.load();
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      }
    }
  }, [currentTrackIndex, data]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(console.error);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const playTrackAtIndex = (index) => {
    setCurrentTrackIndex(index);
    setIsPlaying(true);
    if (audioRef.current) {
      audioRef.current.src = getAudioUrl(data.tracks[index].file_path);
      audioRef.current.load();
      audioRef.current.play().catch(console.error);
    }
  };

  const handleSeek = (e) => {
    const newTime = (e.target.value / 100) * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (loading) {
    return (
      <div className="share-page">
        <div className="share-loading">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="share-page">
        <div className="share-error">
          <h1>🎵 Disney Music</h1>
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  const currentTrack = data.type === 'track' 
    ? data.track 
    : data.tracks[currentTrackIndex];

  const coverUrl = data.type === 'track' 
    ? data.track.cover_url 
    : data.album.cover_url;

  return (
    <div className="share-page">
      <div className="share-container">
        <div className="share-header">
          <h1>🎵 Disney Music</h1>
          <p>Un ami partage {data.type === 'album' ? 'un album' : 'une piste'} avec vous</p>
        </div>

        <div className="share-player">
          {coverUrl && (
            <img 
              src={getCoverUrl(coverUrl)}
              alt={data.type === 'album' ? data.album.title : data.track.album_title}
              className="share-cover"
            />
          )}

          <div className="share-info">
            <h2>{data.type === 'album' ? data.album.title : currentTrack.title}</h2>
            <p className="share-artist">
              {data.type === 'album' ? data.album.artist : currentTrack.artist}
            </p>
            {data.type === 'album' && (
              <p className="share-now-playing">
                ▶ {currentTrack.title}
              </p>
            )}
          </div>

          <audio ref={audioRef} />

          <div className="share-controls">
            {data.type === 'album' && (
              <button 
                onClick={() => currentTrackIndex > 0 && playTrackAtIndex(currentTrackIndex - 1)}
                className="share-nav-btn"
                disabled={currentTrackIndex === 0}
              >
                <FaStepBackward size={20} />
              </button>
            )}
            <button onClick={togglePlay} className="share-play-btn">
              {isPlaying ? <FaPause size={32} /> : <FaPlay size={32} />}
            </button>
            {data.type === 'album' && (
              <button 
                onClick={() => currentTrackIndex < data.tracks.length - 1 && playTrackAtIndex(currentTrackIndex + 1)}
                className="share-nav-btn"
                disabled={currentTrackIndex >= data.tracks.length - 1}
              >
                <FaStepForward size={20} />
              </button>
            )}
          </div>

          <div className="share-progress">
            <span className="share-time">{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max="100"
              value={progress}
              onChange={handleSeek}
              className="share-progress-bar"
            />
            <span className="share-time">{formatTime(duration)}</span>
          </div>

          <div className="share-volume">
            <FaVolumeUp />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              className="share-volume-slider"
            />
          </div>

          {/* Liste des pistes pour les albums */}
          {data.type === 'album' && data.tracks.length > 0 && (
            <div className="share-tracklist">
              <h3>Pistes ({data.tracks.length})</h3>
              {data.tracks.map((track, index) => (
                <div 
                  key={track.id}
                  className={`share-track-item ${index === currentTrackIndex ? 'active' : ''}`}
                  onClick={() => playTrackAtIndex(index)}
                >
                  <span className="share-track-num">{track.track_number}</span>
                  <div className="share-track-info">
                    <span className="share-track-title">{track.title}</span>
                    <span className="share-track-artist">{track.artist}</span>
                  </div>
                  <span className="share-track-duration">{formatTime(track.duration)}</span>
                </div>
              ))}
            </div>
          )}

          <div className="share-meta">
            {data.playsRemaining !== null && (
              <p className="share-plays">
                🎧 {data.playsRemaining} lecture{data.playsRemaining > 1 ? 's' : ''} restante{data.playsRemaining > 1 ? 's' : ''}
              </p>
            )}
            {data.expiresAt && (
              <p className="share-expires">
                ⏰ Expire le {formatDate(data.expiresAt)}
              </p>
            )}
          </div>
        </div>

        <div className="share-footer">
          <p>Créez votre propre bibliothèque musicale Disney sur <strong>disney-music.gpworld.fr</strong></p>
        </div>
      </div>
    </div>
  );
}

export default SharePage;
